import { useQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { modelQuery } from '@/features/ml/models/model-api';
import {
  ModelVersionCreatePage,
  type VersionOriginSearch,
} from '@/features/ml/models/model-version-create-page';
import { ConsoleShell } from '@/features/shell/console-shell';

const text = (value: unknown) => (typeof value === 'string' && value !== '' ? value : undefined);

/**
 * Nouvelle version d'un modèle. Venue d'un run (« Enregistrer une version »), elle porte son run
 * d'origine dans l'URL : `?run=&experiment=&project=`.
 */
export const Route = createFileRoute('/$tenant/models/$modelId/versions/new')({
  validateSearch: (search: Record<string, unknown>): VersionOriginSearch => {
    const run = text(search['run']);
    const experiment = text(search['experiment']);
    const project = text(search['project']);
    return {
      ...(run ? { run } : {}),
      ...(experiment ? { experiment } : {}),
      ...(project ? { project } : {}),
    };
  },
  component: NewVersionRoute,
});

function NewVersionRoute() {
  const { tenant, modelId } = Route.useParams();
  const origin = Route.useSearch();
  const { t } = useTranslation();
  const model = useQuery(modelQuery(tenant, modelId));
  return (
    <ConsoleShell
      tenantId={tenant}
      activeItem="models"
      crumbs={[
        { label: t('nav.items.models') },
        { label: model.data?.name ?? modelId, mono: true },
        { label: t('ml.versions.new') },
      ]}
    >
      <ModelVersionCreatePage tenantId={tenant} modelId={modelId} origin={origin} />
    </ConsoleShell>
  );
}
